import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["styles"],
  _excluded2 = ["styles"],
  _excluded3 = ["styles"];
import { ContainerTypename, GridLayoutTypename, ModuleTypename } from '../../__generated__/chirp/com/hubspot/content/grid/layouts/rpc/GridLayoutsRpc';
import { isContainerNode, isModuleNode } from './utils';
import BaseGridLayoutDataTree from './BaseGridLayoutDataTree';
import { CONTAINER, EDITOR_PROVIDED_MODULE_STYLES, GRID_NODE_TYPES, MODULE, OLD_MODULE_STYLES, SECTION, STYLE_FIELDS_MODULE_STYLES } from './constants';
import { computeGridLayoutTreeDiff } from './diffUtils';
import { isContainer } from './typenameUtils';
export const importGridLayoutJsonToRootNodeValue = gridLayout => {
  return {
    label: gridLayout.label,
    name: gridLayout.name,
    type: gridLayout.type
  };
};
export const importSectionJsonToSectionNodeValue = section => {
  const value = {
    label: section.label,
    styles: section.styles,
    id: section.id,
    type: section.type
  };
  const childContainerVariant = isContainer(section.child) ? section.child.variant : undefined;
  if (section.variant !== undefined) {
    value.variant = section.variant;
  } else if (childContainerVariant !== undefined) {
    value.variant = childContainerVariant;
  }
  if (section.dataVariant !== undefined) {
    value.dataVariant = section.dataVariant;
  }
  if (section.anchor !== undefined) {
    value.anchor = section.anchor;
  }
  return value;
};
export const importContainerJsonToContainerNodeValue = container => {
  const value = {
    label: container.label,
    styles: container.styles,
    id: container.id,
    type: container.type,
    layout: container.layout
  };
  if (container.variant !== undefined) {
    value.variant = container.variant;
  }
  return value;
};
export const importModuleJsonToModuleNodeValue = module => {
  const value = {
    label: module.label,
    styles: module.styles,
    id: module.id,
    type: module.type,
    fieldOverrides: module.fieldOverrides,
    moduleId: module.moduleId,
    path: module.path,
    jsModule: module.jsModule
  };
  if (module.fieldConfigOverrides !== undefined) {
    value.fieldConfigOverrides = module.fieldConfigOverrides;
  }
  return value;
};
const copyObjectWithoutUndefined = obj => {
  const copy = Object.assign({}, obj);
  // Explicitly using mutation to avoid creating new objects
  for (const key in copy) {
    if (copy[key] === undefined) {
      delete copy[key];
    }
  }
  return copy;
};

// This util adds explicity type to breakpoint objects (default, mobile, tablet, etc.)
const serializeBreakpointObject = (breakpointObject, type) => {
  if (!breakpointObject || typeof breakpointObject !== 'object') {
    return breakpointObject;
  }
  const result = Object.assign({}, breakpointObject);
  for (const breakpointKey in result) {
    if (typeof result[breakpointKey] === 'object') {
      result[breakpointKey] = Object.assign({}, result[breakpointKey], {
        type
      });
    }
  }
  return result;
};
const getGridLayoutJsonForGridContentNode = node => {
  if (isContainerNode(node)) {
    const _node$getValue = node.getValue(),
      {
        styles
      } = _node$getValue,
      rest = _objectWithoutPropertiesLoose(_node$getValue, _excluded);
    const container = copyObjectWithoutUndefined(Object.assign({}, rest, {
      __typename: ContainerTypename,
      styles: {
        type: CONTAINER,
        breakpointStyles: serializeBreakpointObject(styles.breakpointStyles, CONTAINER)
      },
      children: node.getChildNodes().map(getGridLayoutJsonForGridContentNode)
    }));
    // Probably a better way to do this, TS has trouble inferring the type of
    // the return type
    return container;
  } else if (isModuleNode(node)) {
    var _styles$editorProvide;
    // Probably a better way to do this, TS has trouble inferring the type of
    // the return type
    const _node$getValue2 = node.getValue(),
      {
        styles
      } = _node$getValue2,
      rest = _objectWithoutPropertiesLoose(_node$getValue2, _excluded2);
    const moduleStyleObject = {
      // @ts-expect-error 'type' needed for API
      type: MODULE
    };
    if ((_styles$editorProvide = styles.editorProvidedModuleBreakpointStyles) !== null && _styles$editorProvide !== void 0 && _styles$editorProvide.breakpointStyles) {
      moduleStyleObject.editorProvidedModuleBreakpointStyles = {
        // @ts-expect-error 'type' needed for API
        type: EDITOR_PROVIDED_MODULE_STYLES,
        breakpointStyles: serializeBreakpointObject(styles.editorProvidedModuleBreakpointStyles.breakpointStyles, EDITOR_PROVIDED_MODULE_STYLES)
      };
    }
    if (styles.oldModuleStyles) {
      moduleStyleObject.oldModuleStyles = Object.assign({}, styles.oldModuleStyles, {
        // @ts-expect-error 'type' needed for API
        type: OLD_MODULE_STYLES
      });
    }
    if (styles.styleFieldsModuleBreakpointStyles) {
      moduleStyleObject.styleFieldsModuleBreakpointStyles = {
        // @ts-expect-error 'type' needed for API
        type: STYLE_FIELDS_MODULE_STYLES,
        breakpointStyles: serializeBreakpointObject(styles.styleFieldsModuleBreakpointStyles.breakpointStyles, STYLE_FIELDS_MODULE_STYLES)
      };
    }
    return copyObjectWithoutUndefined(Object.assign({
      styles: moduleStyleObject,
      __typename: ModuleTypename
    }, rest));
  }
  throw new Error('Invalid node');
};

/**
 * Used for exporting a tree to the buffer for editor 1.0.
 * @param tree
 * @returns GridLayout
 */
export const exportForGridLayoutDataApi = tree => {
  const gridLayout = {
    label: tree.getRootNode().getValue().label,
    name: tree.getRootNode().getValue().name,
    type: tree.getRootNode().getValue().type,
    content: [],
    __typename: GridLayoutTypename
  };
  const gridLayoutContent = tree.getRootNode().getChildNodes().map(node => {
    const _node$getValue3 = node.getValue(),
      {
        styles
      } = _node$getValue3,
      rest = _objectWithoutPropertiesLoose(_node$getValue3, _excluded3);
    return copyObjectWithoutUndefined(Object.assign({}, rest, {
      styles: {
        type: SECTION,
        breakpointStyles: serializeBreakpointObject(styles.breakpointStyles, SECTION)
      },
      child: getGridLayoutJsonForGridContentNode(node.getChildContainer())
    }));
  });
  gridLayout.content = gridLayoutContent;
  return gridLayout;
};

// Not sure yet if we want to conflate tree export and tree diffing as being the
// same thing moving forward. In layout sections, they're different, and from
// what I can tell it seems to be just as a micro optimization. We might be able
// to get away with just having these be the same thing from now on.
export const exportTree = computeGridLayoutTreeDiff;
export const importTree = (exportedTree, prevTree) => {
  const {
    rootNode,
    addedSections,
    removedSections,
    changedSections,
    addedContainers,
    removedContainers,
    changedContainers,
    addedModules,
    removedModules,
    changedModules
  } = exportedTree;

  // Assumes that there will always be a difference with the tree itself
  // whenever `importTree` is called, so we always make a new instance
  const newTree = new BaseGridLayoutDataTree({
    rootId: rootNode.id,
    rootNodeValue: rootNode.value,
    initialState: prevTree === null || prevTree === void 0 ? void 0 : prevTree.getCurrentState()
  });
  newTree.setState(draft => {
    addedSections.forEach(section => {
      var _section$childNodeIds;
      draft.sectionsById[section.id] = newTree.createNewSection({
        id: section.id,
        type: GRID_NODE_TYPES.SECTION,
        value: section.value,
        parentNodeId: section.parentNodeId,
        childNodeIds: (_section$childNodeIds = section.childNodeIds) !== null && _section$childNodeIds !== void 0 ? _section$childNodeIds : [],
        treeRef: newTree
      });
    });
    changedSections.forEach(section => {
      var _section$childNodeIds2;
      draft.sectionsById[section.id] = newTree.createNewSection({
        id: section.id,
        type: GRID_NODE_TYPES.SECTION,
        value: section.value,
        parentNodeId: section.parentNodeId,
        childNodeIds: (_section$childNodeIds2 = section.childNodeIds) !== null && _section$childNodeIds2 !== void 0 ? _section$childNodeIds2 : [],
        treeRef: newTree
      });
    });
    addedContainers.forEach(container => {
      var _container$childNodeI;
      draft.containersById[container.id] = newTree.createNewContainer({
        id: container.id,
        type: GRID_NODE_TYPES.CONTAINER,
        value: container.value,
        parentNodeId: container.parentNodeId,
        childNodeIds: (_container$childNodeI = container.childNodeIds) !== null && _container$childNodeI !== void 0 ? _container$childNodeI : [],
        treeRef: newTree
      });
    });
    changedContainers.forEach(container => {
      var _container$childNodeI2;
      draft.containersById[container.id] = newTree.createNewContainer({
        id: container.id,
        type: GRID_NODE_TYPES.CONTAINER,
        value: container.value,
        parentNodeId: container.parentNodeId,
        childNodeIds: (_container$childNodeI2 = container.childNodeIds) !== null && _container$childNodeI2 !== void 0 ? _container$childNodeI2 : [],
        treeRef: newTree
      });
    });
    addedModules.forEach(module => {
      draft.modulesById[module.id] = newTree.createNewModule({
        id: module.id,
        type: GRID_NODE_TYPES.MODULE,
        value: module.value,
        parentNodeId: module.parentNodeId,
        treeRef: newTree
      });
    });
    changedModules.forEach(module => {
      draft.modulesById[module.id] = newTree.createNewModule({
        id: module.id,
        type: GRID_NODE_TYPES.MODULE,
        value: module.value,
        parentNodeId: module.parentNodeId,
        treeRef: newTree
      });
    });
    removedSections.forEach(section => {
      delete draft.sectionsById[section.id];
    });
    removedContainers.forEach(container => {
      delete draft.containersById[container.id];
    });
    removedModules.forEach(module => {
      delete draft.modulesById[module.id];
    });
    if (rootNode.childNodeIds) {
      draft.rootNode._setChildNodeIds(rootNode.childNodeIds);
    }
    return draft;
  });
  newTree.setNodeTreeRefs(newTree);
  return newTree;
};